import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Copy,
  Database,
  ExternalLink,
  Gift,
  KeyRound,
  Layers,
  Mail,
  MessageSquare,
  Power,
  Search,
  Server,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  listContactInquiries,
  updateContactInquiryStatus,
  type ContactInquiryItem,
} from "@/lib/endpoints";
import {
  changeTenantPlan,
  getSaasMetrics,
  impersonateTenant,
  listPlans,
  listSaasTenants,
  onboardTenant,
  upgradeTenantSchema,
  updateTenantServices,
  updateTenantStatus,
  type OnboardingRequest,
  type SaasTenantItem,
} from "@/lib/saas";
import { clearTenantLocalCache } from "@/lib/offline/db";
import { getSession, setSession } from "@/lib/session";

interface CreatedTenantCredentials {
  name: string;
  slug: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
  phone: string | null;
  welcomeEmailQueued: boolean;
}

export function SuperadminScreen() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"tenants" | "inquiries">("tenants");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "trial" | "suspended">("all");

  const { data: inquiries = [], isLoading: isLoadingInquiries } = useQuery({
    queryKey: ["saas", "inquiries"],
    queryFn: () => listContactInquiries(),
  });

  const updateInquiryMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateContactInquiryStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["saas", "inquiries"] });
    },
  });

  // Modals state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingProspectPhone, setOnboardingProspectPhone] = useState<string | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<CreatedTenantCredentials | null>(null);
  const [copiedCredentials, setCopiedCredentials] = useState(false);
  const [planModalTenant, setPlanModalTenant] = useState<SaasTenantItem | null>(null);
  const [selectedNewPlan, setSelectedNewPlan] = useState<string>("pro");
  const [servicesModalTenant, setServicesModalTenant] = useState<SaasTenantItem | null>(null);
  const [tenantServicesForm, setTenantServicesForm] = useState({
    has_pos: true,
    has_restaurant: false,
    has_workshop: false,
  });

  // Onboarding form state
  const [onboardingForm, setOnboardingForm] = useState<OnboardingRequest>({
    slug: "",
    name: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    plan_code: "pro",
    branch_name: "Matriz",
    branch_code: "MTZ",
    rfc: "",
    has_pos: true,
    has_restaurant: false,
    has_workshop: false,
    inquiry_id: null,
  });

  const { data: metrics } = useQuery({
    queryKey: ["saas", "metrics"],
    queryFn: getSaasMetrics,
    refetchInterval: 15_000,
  });

  const { data: tenants = [], isLoading: isLoadingTenants } = useQuery({
    queryKey: ["saas", "tenants"],
    queryFn: listSaasTenants,
    refetchInterval: 15_000,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["saas", "plans"],
    queryFn: listPlans,
  });

  const onboardMutation = useMutation({
    mutationFn: onboardTenant,
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["saas"] });
      setCreatedCredentials({
        name: data.name || variables.name,
        slug: data.slug || variables.slug,
        ownerName: variables.owner_name,
        ownerEmail: variables.owner_email,
        ownerPassword: variables.owner_password,
        phone: onboardingProspectPhone,
        welcomeEmailQueued: Boolean(data.welcome_email_queued ?? true),
      });
      setCopiedCredentials(false);
      setShowOnboardingModal(false);
      resetOnboardingForm();
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ tenantId, isActive }: { tenantId: string; isActive: boolean }) =>
      updateTenantStatus(tenantId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saas"] });
    },
  });

  const planMutation = useMutation({
    mutationFn: ({ tenantId, planCode }: { tenantId: string; planCode: string }) =>
      changeTenantPlan(tenantId, planCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saas"] });
      setPlanModalTenant(null);
    },
  });

  const servicesMutation = useMutation({
    mutationFn: ({
      tenantId,
      services,
    }: {
      tenantId: string;
      services: { has_pos: boolean; has_restaurant: boolean; has_workshop: boolean };
    }) => updateTenantServices(tenantId, services),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saas"] });
      setServicesModalTenant(null);
    },
  });

  const [schemaUpgradeTenant, setSchemaUpgradeTenant] = useState<SaasTenantItem | null>(null);
  const [schemaUpgradeSuccess, setSchemaUpgradeSuccess] = useState<string | null>(null);

  const upgradeSchemaMutation = useMutation({
    mutationFn: (tenantId: string) => upgradeTenantSchema(tenantId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["saas"] });
      setSchemaUpgradeSuccess(data.message);
      setSchemaUpgradeTenant(null);
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: (tenantId: string) => impersonateTenant(tenantId),
    onSuccess: async (data) => {
      const current = getSession();
      if (current) {
        localStorage.setItem("megasyr.impersonator", JSON.stringify(current));
      }
      await clearTenantLocalCache();
      queryClient.clear();
      if (current) {
        setSession({
          ...current,
          tokens: {
            ...current.tokens,
            access_token: data.access_token,
            refresh_token: data.refresh_token ?? current.tokens.refresh_token,
            token_type: "bearer",
            expires_in: data.expires_in,
            refresh_expires_at: data.refresh_expires_at ?? current.tokens.refresh_expires_at,
          },
          tenant: {
            ...current.tenant,
            id: data.target_tenant_id ?? current.tenant.id,
            slug: data.target_tenant_slug,
            name: data.target_tenant_name ?? data.target_tenant_slug,
          },
        });
      }
      window.location.href = "/";
    },
  });

  function resetOnboardingForm() {
    setOnboardingProspectPhone(null);
    setOnboardingForm({
      slug: "",
      name: "",
      owner_name: "",
      owner_email: "",
      owner_password: "",
      plan_code: "pro",
      branch_name: "Matriz",
      branch_code: "MTZ",
      rfc: "",
      has_pos: true,
      has_restaurant: false,
      has_workshop: false,
      inquiry_id: null,
    });
  }

  function toSlug(value: string): string {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
  }

  function handleNameChange(name: string) {
    const slug = toSlug(name);
    setOnboardingForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug === "" || prev.slug === toSlug(prev.name) ? slug : prev.slug,
    }));
  }

  function openOnboardingFromInquiry(inq: ContactInquiryItem) {
    const rawBusinessName = (inq.business_name || inq.full_name || "Negocio").trim();
    const generatedSlug = toSlug(rawBusinessName) || `negocio-${inq.id.slice(0, 6)}`;
    const bType = (inq.business_type || "pos").toLowerCase();

    setOnboardingProspectPhone(inq.phone ?? null);
    setOnboardingForm({
      slug: generatedSlug.length >= 2 ? generatedSlug : `${generatedSlug}-mx`,
      name: rawBusinessName,
      owner_name: inq.full_name.trim(),
      owner_email: inq.email.trim().toLowerCase(),
      owner_password: "MegaSyR2026*",
      plan_code: "pro",
      branch_name: "Matriz",
      branch_code: "MTZ",
      rfc: "",
      has_pos: true,
      has_restaurant: bType === "restaurant" || bType === "multiple",
      has_workshop: bType === "workshop" || bType === "multiple",
      inquiry_id: inq.id,
    });
    setShowOnboardingModal(true);
  }

  const filteredTenants = tenants.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.rfc && t.rfc.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;
    if (statusFilter === "active") return t.is_active && t.subscription_status === "active";
    if (statusFilter === "trial") return t.subscription_status === "trial";
    if (statusFilter === "suspended") return !t.is_active || t.subscription_status === "suspended";
    return true;
  });

  return (
    <div className="space-y-6 p-6">
      {/* Encabezado y Acción de Onboarding */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-lg bg-electric/20 text-electric">
              <ShieldCheck className="size-4" />
            </span>
            <h1 className="font-display text-2xl font-bold tracking-tight text-carbon">
              Superadministrador SaaS
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted">
            Control global de suscripciones, límites de recursos, altas y sesiones de clientes
          </p>
        </div>

        <Button
          onClick={() => {
            resetOnboardingForm();
            setShowOnboardingModal(true);
          }}
          className="flex items-center gap-2"
        >
          <Sparkles className="size-4" />
          Onboarding Rápido (&lt; 10 min)
        </Button>
      </div>

      {schemaUpgradeSuccess && (
        <div className="flex items-center justify-between rounded-msr border border-cyan-500/30 bg-cyan-500/10 p-4 text-sm text-cyan-800 dark:text-cyan-200">
          <div className="flex items-center gap-2">
            <CheckCircle className="size-5 text-cyan-600 dark:text-cyan-400" />
            <span>{schemaUpgradeSuccess}</span>
          </div>
          <button
            type="button"
            onClick={() => setSchemaUpgradeSuccess(null)}
            className="text-cyan-600 hover:text-cyan-800 dark:text-cyan-400 dark:hover:text-white"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {/* Tarjetas de Métricas de Plataforma (KPIs) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              MRR Plataforma
            </span>
            <Building2 className="size-4 text-emerald-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {metrics ? <Money cents={metrics.total_mrr_cents} /> : "—"}
          </p>
          <span className="text-xs text-muted">Facturación mensual estimada</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Negocios Activos
            </span>
            <CheckCircle className="size-4 text-emerald-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {metrics?.active_tenants ?? 0}
          </p>
          <span className="text-xs text-muted">Operando normalmente</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Periodo Trial
            </span>
            <Sparkles className="size-4 text-amber-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {metrics?.trial_tenants ?? 0}
          </p>
          <span className="text-xs text-muted">Prueba gratuita activa</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Suspendidos
            </span>
            <ShieldAlert className="size-4 text-rose-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {metrics?.suspended_tenants ?? 0}
          </p>
          <span className="text-xs text-muted">Acceso revocado</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Usuarios Totales
            </span>
            <Users className="size-4 text-cyan-600 dark:text-cyan-400" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {metrics?.total_users ?? 0}
          </p>
          <span className="text-xs text-muted">Cuentas creadas</span>
        </Card>
      </div>

      {/* Pestañas Principales: Negocios vs Solicitudes de Contacto y Pruebas */}
      <div className="flex border-b border-line gap-6 pt-2">
        <button
          type="button"
          onClick={() => setActiveTab("tenants")}
          className={cn(
            "pb-3 text-sm font-bold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "tenants"
              ? "border-electric text-electric"
              : "border-transparent text-muted hover:text-carbon"
          )}
        >
          <Building2 className="size-4" />
          <span>Negocios Registrados ({tenants.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("inquiries")}
          className={cn(
            "pb-3 text-sm font-bold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "inquiries"
              ? "border-electric text-electric"
              : "border-transparent text-muted hover:text-carbon"
          )}
        >
          <Mail className="size-4" />
          <span>Solicitudes de Contacto y Pruebas ({inquiries.length})</span>
          {inquiries.filter((i) => i.status === "new").length > 0 && (
            <span className="rounded-full bg-electric px-2 py-0.5 text-[11px] font-bold text-white">
              {inquiries.filter((i) => i.status === "new").length} nuevas
            </span>
          )}
        </button>
      </div>

      {activeTab === "tenants" ? (
        <>
          {/* Filtros y Buscador */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Buscar por nombre, slug o RFC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-4 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              {(
                [
                  { id: "all", label: "Todos" },
                  { id: "active", label: "Activos" },
                  { id: "trial", label: "Trial" },
                  { id: "suspended", label: "Suspendidos" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`rounded-msr px-3 py-1.5 text-xs font-medium transition-colors ${
                    statusFilter === tab.id
                      ? "bg-electric text-white font-semibold"
                      : "border border-line bg-snow text-muted hover:bg-surface hover:text-carbon"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tabla de Tenants */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-carbon">
                <thead className="border-b border-line bg-surface text-xs uppercase tracking-wider text-muted font-semibold">
                  <tr>
                    <th className="px-4 py-3">Negocio / Identificador</th>
                    <th className="px-4 py-3">Plan Actual</th>
                    <th className="px-4 py-3">Base de Datos</th>
                    <th className="px-4 py-3">Servicios</th>
                    <th className="px-4 py-3">Usuarios</th>
                    <th className="px-4 py-3">Sucursales</th>
                    <th className="px-4 py-3">Productos</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {isLoadingTenants ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-muted">
                        Cargando negocios...
                      </td>
                    </tr>
                  ) : filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-muted">
                        No se encontraron negocios con los filtros actuales
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((t) => {
                      const userPct = Math.round((t.users_count / t.max_users) * 100);
                      const branchPct = Math.round((t.branches_count / t.max_branches) * 100);
                      const prodPct = Math.round((t.products_count / t.max_products) * 100);

                      return (
                        <tr key={t.id} className="transition-colors hover:bg-surface/60">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-carbon">{t.name}</div>
                            <div className="text-xs text-muted font-mono">
                              {t.slug} {t.rfc ? `· ${t.rfc}` : ""}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric/10 px-2.5 py-0.5 text-xs font-semibold text-electric capitalize">
                              {t.plan_name}
                            </span>
                          </td>

                          {/* Arquitectura de Base de Datos */}
                          <td className="px-4 py-3">
                            {t.schema_mode === "dedicated" ? (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center gap-1 rounded bg-cyan-500/15 px-2 py-0.5 text-[10px] font-bold text-cyan-600 dark:text-cyan-300 border border-cyan-500/30">
                                  <Database className="size-3" />
                                  Opción B · Dedicado
                                </span>
                                <p className="font-mono text-[10px] text-muted">{t.schema_name}</p>
                              </div>
                            ) : (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center gap-1 rounded bg-surface px-2 py-0.5 text-[10px] font-bold text-muted border border-line">
                                  <Server className="size-3" />
                                  Opción A · Compartido
                                </span>
                                <p className="text-[10px] text-muted">Prueba 30 días</p>
                              </div>
                            )}
                          </td>

                          {/* Servicios Habilitados */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1">
                              {t.has_pos !== false && (
                                <span className="inline-flex items-center rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                                  POS
                                </span>
                              )}
                              {t.has_restaurant && (
                                <span className="inline-flex items-center rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 border border-amber-500/30">
                                  Restaurante
                                </span>
                              )}
                              {t.has_workshop && (
                                <span className="inline-flex items-center rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-400 border border-violet-500/30">
                                  Taller
                                </span>
                              )}
                              {!t.has_pos && !t.has_restaurant && !t.has_workshop && (
                                <span className="text-[10px] text-muted italic">Ninguno</span>
                              )}
                            </div>
                          </td>

                          {/* Contador Usuarios */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-between text-xs text-carbon">
                              <span>
                                {t.users_count} / {t.max_users}
                              </span>
                              <span className="text-[10px] text-muted">{userPct}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-line">
                              <div
                                className={`h-full ${
                                  userPct >= 100
                                    ? "bg-rose-500"
                                    : userPct >= 80
                                    ? "bg-amber-400"
                                    : "bg-emerald-400"
                                }`}
                                style={{ width: `${Math.min(userPct, 100)}%` }}
                              />
                            </div>
                          </td>

                          {/* Contador Sucursales */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-between text-xs text-carbon">
                              <span>
                                {t.branches_count} / {t.max_branches}
                              </span>
                              <span className="text-[10px] text-muted">{branchPct}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-line">
                              <div
                                className={`h-full ${
                                  branchPct >= 100
                                    ? "bg-rose-500"
                                    : branchPct >= 80
                                    ? "bg-amber-400"
                                    : "bg-cyan-400"
                                }`}
                                style={{ width: `${Math.min(branchPct, 100)}%` }}
                              />
                            </div>
                          </td>

                          {/* Contador Productos */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-between text-xs text-carbon">
                              <span>
                                {t.products_count} / {t.max_products}
                              </span>
                              <span className="text-[10px] text-muted">{prodPct}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-line">
                              <div
                                className={`h-full ${
                                  prodPct >= 100
                                    ? "bg-rose-500"
                                    : prodPct >= 80
                                    ? "bg-amber-400"
                                    : "bg-violet-400"
                                }`}
                                style={{ width: `${Math.min(prodPct, 100)}%` }}
                              />
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            {t.is_active ? (
                              t.subscription_status === "trial" ? (
                                <StatusPill tone="warning">Trial</StatusPill>
                              ) : (
                                <StatusPill tone="ok">Activo</StatusPill>
                              )
                            ) : (
                              <StatusPill tone="danger">Suspendido</StatusPill>
                            )}
                          </td>

                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setServicesModalTenant(t);
                                  setTenantServicesForm({
                                    has_pos: t.has_pos !== false,
                                    has_restaurant: Boolean(t.has_restaurant),
                                    has_workshop: Boolean(t.has_workshop),
                                  });
                                }}
                                title="Administrar Servicios (POS, Restaurante, Taller)"
                                className="rounded p-1.5 text-muted hover:bg-surface hover:text-carbon transition-colors"
                              >
                                <SlidersHorizontal className="size-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setPlanModalTenant(t);
                                  setSelectedNewPlan(t.plan_code);
                                }}
                                title="Cambiar Plan"
                                className="rounded p-1.5 text-muted hover:bg-surface hover:text-carbon transition-colors"
                              >
                                <Layers className="size-4" />
                              </button>

                              {t.schema_mode !== "dedicated" && (
                                <button
                                  type="button"
                                  onClick={() => setSchemaUpgradeTenant(t)}
                                  title="Migrar a Esquema Dedicado (Opción B)"
                                  className="rounded p-1.5 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
                                >
                                  <Database className="size-4" />
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() =>
                                  statusMutation.mutate({
                                    tenantId: t.id,
                                    isActive: !t.is_active,
                                  })
                                }
                                title={t.is_active ? "Suspender Negocio" : "Reactivar Negocio"}
                                className={`rounded p-1.5 transition-colors ${
                                  t.is_active
                                    ? "text-rose-500 hover:bg-rose-500/10"
                                    : "text-emerald-500 hover:bg-emerald-500/10"
                                }`}
                              >
                                <Power className="size-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() => impersonateMutation.mutate(t.id)}
                                disabled={!t.is_active || impersonateMutation.isPending}
                                title="Impersonar (Iniciar sesión como cliente)"
                                className="flex items-center gap-1 rounded bg-electric/10 px-2.5 py-1 text-xs font-semibold text-electric hover:bg-electric/20 disabled:opacity-40 transition-colors"
                              >
                                <UserCheck className="size-3.5" />
                                Entrar
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
    ) : (
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-carbon">
            <thead className="border-b border-line bg-surface text-xs font-semibold uppercase tracking-wider text-muted">
              <tr>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Prospecto y Negocio</th>
                <th className="px-4 py-3">Giro</th>
                <th className="px-4 py-3">¿Prueba Gratis?</th>
                <th className="px-4 py-3">Contacto Directo</th>
                <th className="px-4 py-3">Mensaje</th>
                <th className="px-4 py-3">Correo Saliente</th>
                <th className="px-4 py-3 text-right">Estatus / Gestión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {isLoadingInquiries ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted">
                    Cargando solicitudes...
                  </td>
                </tr>
              ) : inquiries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted">
                    No hay solicitudes de contacto ni pruebas gratis registradas aún.
                  </td>
                </tr>
              ) : (
                inquiries.map((inq) => {
                  const cleanPhone = (inq.phone || "").replace(/\D/g, "");
                  const waLink =
                    cleanPhone.length >= 10
                      ? `https://wa.me/52${cleanPhone.slice(-10)}?text=Hola%20${encodeURIComponent(
                          inq.full_name
                        )},%20te%20contacto%20de%20MegaSyR%20respecto%20a%20tu%20solicitud.`
                      : null;

                  return (
                    <tr key={inq.id} className="hover:bg-surface/60 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">
                        {inq.created_at
                          ? new Date(inq.created_at).toLocaleDateString("es-MX", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-carbon">{inq.full_name}</p>
                        <p className="text-xs text-muted">
                          {inq.business_name || "Sin negocio especificado"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="rounded bg-surface border border-line px-2 py-0.5 text-carbon">
                          {inq.business_type === "pos"
                            ? "Punto de Venta"
                            : inq.business_type === "restaurant"
                            ? "Restaurante"
                            : inq.business_type === "workshop"
                            ? "Taller"
                            : inq.business_type === "multiple"
                            ? "Múltiples"
                            : inq.business_type || "General"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {inq.is_free_trial_requested ? (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-emerald-600 dark:text-emerald-400 font-bold">
                            <Gift className="size-3" />
                            <span>Prueba 15 Días</span>
                          </span>
                        ) : (
                          <span className="text-muted">Solo consulta</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs space-y-1">
                        <div className="flex items-center gap-1.5 text-muted">
                          <Mail className="size-3 text-muted" />
                          <a href={`mailto:${inq.email}`} className="text-electric hover:underline font-medium">
                            {inq.email}
                          </a>
                        </div>
                        {inq.phone ? (
                          <div className="flex items-center gap-2">
                            <span className="text-muted">{inq.phone}</span>
                            {waLink ? (
                              <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-500 transition-colors"
                              >
                                <MessageSquare className="size-3" />
                                <span>WhatsApp</span>
                              </a>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs max-w-xs text-muted truncate" title={inq.message || ""}>
                        {inq.message || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {inq.email_sent ? (
                          <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                            <CheckCircle className="size-3.5" /> Enviado
                          </span>
                        ) : inq.email_error ? (
                          <span className="text-rose-500 font-semibold" title={inq.email_error}>
                            Fallo ({inq.email_error.slice(0, 15)}...)
                          </span>
                        ) : (
                          <span className="text-muted">Pendiente</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {inq.status !== "converted" ? (
                            <button
                              type="button"
                              onClick={() => openOnboardingFromInquiry(inq)}
                              title="Pre-llenar y crear cuenta de prueba en 1 clic"
                              className="inline-flex items-center gap-1 rounded border border-electric/40 bg-electric/15 px-2.5 py-1 text-xs font-bold text-electric hover:bg-electric hover:text-white transition-colors whitespace-nowrap"
                            >
                              <Sparkles className="size-3" />
                              <span>Crear Cuenta</span>
                            </button>
                          ) : null}
                          <select
                            value={inq.status}
                            onChange={(e) =>
                              updateInquiryMutation.mutate({
                                id: inq.id,
                                status: e.target.value,
                              })
                            }
                            className={cn(
                              "rounded px-2.5 py-1 text-xs font-semibold border focus:outline-none bg-snow border-line",
                              inq.status === "new"
                                ? "text-amber-600 dark:text-amber-400"
                                : inq.status === "contacted"
                                ? "text-cyan-600 dark:text-cyan-400"
                                : "text-emerald-600 dark:text-emerald-400"
                            )}
                          >
                            <option value="new">Nueva</option>
                            <option value="contacted">Contactada</option>
                            <option value="converted">Convertida a Cliente</option>
                            <option value="discarded">Descartada</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    )}

      {/* Modal de Onboarding Rápido */}
      {showOnboardingModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="size-5 text-electric" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  Alta de Negocio (Onboarding &lt; 10 min)
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowOnboardingModal(false)}
                className="text-muted hover:text-carbon transition-colors"
              >
                <X className="size-5" />
              </button>
            </div>

            {onboardingForm.inquiry_id ? (
              <div className="rounded-msr border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                <Gift className="size-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <span className="font-bold">Pre-llenado desde Solicitud de Prueba Gratis.</span>{" "}
                  Al crear el negocio se enviará el correo de bienvenida con accesos y la solicitud se marcará automáticamente como <strong>Convertida a Cliente</strong>.
                </div>
              </div>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                onboardMutation.mutate(onboardingForm);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-muted">
                    Nombre del Negocio *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Taller Los Pinos"
                    value={onboardingForm.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="mt-1 w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted">
                    Identificador / Slug URL *
                  </label>
                  <input
                    type="text"
                    required
                    pattern="^[a-z0-9][a-z0-9-]{1,62}$"
                    placeholder="taller-los-pinos"
                    value={onboardingForm.slug}
                    onChange={(e) =>
                      setOnboardingForm((prev) => ({ ...prev, slug: e.target.value }))
                    }
                    className="mt-1 w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm font-mono text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-muted">
                    Nombre del Dueño / Admin *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Carlos Mendoza"
                    value={onboardingForm.owner_name}
                    onChange={(e) =>
                      setOnboardingForm((prev) => ({ ...prev, owner_name: e.target.value }))
                    }
                    className="mt-1 w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted">
                    Correo del Dueño *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="carlos@taller.mx"
                    value={onboardingForm.owner_email}
                    onChange={(e) =>
                      setOnboardingForm((prev) => ({ ...prev, owner_email: e.target.value }))
                    }
                    className="mt-1 w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-semibold text-muted">
                    Contraseña Inicial *
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setOnboardingForm((prev) => ({
                        ...prev,
                        owner_password: "MegaSyR2026*",
                      }))
                    }
                    className="text-[11px] font-semibold text-electric hover:underline inline-flex items-center gap-1"
                  >
                    <KeyRound className="size-3" />
                    Usar MegaSyR2026*
                  </button>
                </div>
                <input
                  type="text"
                  required
                  minLength={8}
                  placeholder="Mínimo 8 caracteres"
                  value={onboardingForm.owner_password}
                  onChange={(e) =>
                    setOnboardingForm((prev) => ({
                      ...prev,
                      owner_password: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm font-mono text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                />
              </div>

              {/* Selector de Plan */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Plan Inicial
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {plans.map((p) => (
                    <button
                      type="button"
                      key={p.code}
                      onClick={() =>
                        setOnboardingForm((prev) => ({ ...prev, plan_code: p.code }))
                      }
                      className={`flex flex-col items-center justify-center rounded-msr border p-2 text-center transition-all ${
                        onboardingForm.plan_code === p.code
                          ? "border-electric bg-electric/15 text-electric font-bold"
                          : "border-line bg-surface text-muted hover:bg-surface/80 hover:text-carbon"
                      }`}
                    >
                      <span className="text-xs capitalize">{p.name}</span>
                      <span className="text-[10px] text-muted">
                        <Money cents={p.price_monthly_cents} />/mes
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selector de Servicios Habilitados */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-1.5">
                  Servicios y Módulos Habilitados
                </label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <label
                    className={`flex items-start gap-2.5 rounded-msr border p-2.5 cursor-pointer transition-colors ${
                      onboardingForm.has_pos !== false
                        ? "border-emerald-500/50 bg-emerald-500/10 text-carbon"
                        : "border-line bg-surface text-muted"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={onboardingForm.has_pos ?? true}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({ ...prev, has_pos: e.target.checked }))
                      }
                      className="mt-0.5 rounded border-line text-emerald-500 focus:ring-0"
                    />
                    <div>
                      <div className="text-xs font-bold text-carbon">Punto de Venta</div>
                      <div className="text-[10px] text-muted">Mostrador y ventas</div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-2.5 rounded-msr border p-2.5 cursor-pointer transition-colors ${
                      onboardingForm.has_restaurant
                        ? "border-amber-500/50 bg-amber-500/10 text-carbon"
                        : "border-line bg-surface text-muted"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(onboardingForm.has_restaurant)}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          has_restaurant: e.target.checked,
                        }))
                      }
                      className="mt-0.5 rounded border-line text-amber-500 focus:ring-0"
                    />
                    <div>
                      <div className="text-xs font-bold text-carbon">Restaurante</div>
                      <div className="text-[10px] text-muted">Mesas y comandas</div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-2.5 rounded-msr border p-2.5 cursor-pointer transition-colors ${
                      onboardingForm.has_workshop
                        ? "border-violet-500/50 bg-violet-500/10 text-carbon"
                        : "border-line bg-surface text-muted"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(onboardingForm.has_workshop)}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          has_workshop: e.target.checked,
                        }))
                      }
                      className="mt-0.5 rounded border-line text-violet-500 focus:ring-0"
                    />
                    <div>
                      <div className="text-xs font-bold text-carbon">Taller</div>
                      <div className="text-[10px] text-muted">Órdenes mecánicas</div>
                    </div>
                  </label>
                </div>
                <p className="mt-1.5 text-[11px] text-muted">
                  Se enviará automáticamente un correo de bienvenida con las credenciales desde <strong>contacto@megasyr.mx</strong> al correo del dueño.
                </p>
              </div>

              {onboardMutation.isError ? (
                <div className="rounded bg-rose-500/10 p-3 text-xs text-rose-500 flex items-center gap-2">
                  <AlertTriangle className="size-4 shrink-0" />
                  <span>
                    {(onboardMutation.error as Error)?.message ||
                      "Error al provisionar el negocio"}
                  </span>
                </div>
              ) : null}

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowOnboardingModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={onboardMutation.isPending}>
                  {onboardMutation.isPending ? "Provisionando..." : "Crear y Enviar Accesos"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal de Credenciales Creadas y Envío por WhatsApp */}
      {createdCredentials ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="size-5 text-emerald-500" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  ¡Negocio Creado con Éxito!
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setCreatedCredentials(null)}
                className="text-muted hover:text-carbon transition-colors"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="rounded-msr border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
              <Mail className="size-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
              <div>
                Se ha enviado automáticamente el correo de bienvenida con los accesos desde{" "}
                <strong>contacto@megasyr.mx</strong> a{" "}
                <strong>{createdCredentials.ownerEmail}</strong>.
              </div>
            </div>

            {(() => {
              const directLoginUrl = `https://megasyr.mx/login?tenant=${encodeURIComponent(
                createdCredentials.slug
              )}&email=${encodeURIComponent(createdCredentials.ownerEmail)}`;
              const shareText = `¡Hola ${createdCredentials.ownerName}! 🎉\n\nTu cuenta de *MegaSyR* para *${createdCredentials.name}* ya está activa con tus 15 días de prueba gratis.\n\n🔑 *TUS DATOS DE ACCESO:*\n• *Enlace directo:* ${directLoginUrl}\n• *Negocio (Identificador):* ${createdCredentials.slug}\n• *Correo:* ${createdCredentials.ownerEmail}\n• *Contraseña inicial:* ${createdCredentials.ownerPassword}\n\nCualquier duda con tu configuración estamos para apoyarte desde Tlalixcoyan, Veracruz.`;
              const cleanPhone = (createdCredentials.phone || "").replace(/\D/g, "");
              const waHref =
                cleanPhone.length >= 10
                  ? `https://wa.me/52${cleanPhone.slice(-10)}?text=${encodeURIComponent(shareText)}`
                  : `https://wa.me/?text=${encodeURIComponent(shareText)}`;

              return (
                <>
                  <div className="rounded-msr border border-line bg-surface p-4 space-y-2.5 text-xs">
                    <div className="flex justify-between gap-2 border-b border-line pb-2">
                      <span className="text-muted">Negocio:</span>
                      <span className="font-bold text-carbon">{createdCredentials.name}</span>
                    </div>
                    <div className="flex justify-between gap-2 border-b border-line pb-2">
                      <span className="text-muted">Identificador (Slug):</span>
                      <span className="font-mono font-bold text-electric">
                        {createdCredentials.slug}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 border-b border-line pb-2">
                      <span className="text-muted">Correo del Dueño:</span>
                      <span className="font-semibold text-carbon">
                        {createdCredentials.ownerEmail}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 border-b border-line pb-2">
                      <span className="text-muted">Contraseña Inicial:</span>
                      <span className="font-mono font-bold text-amber-600 dark:text-amber-300">
                        {createdCredentials.ownerPassword}
                      </span>
                    </div>
                    <div className="pt-1">
                      <span className="text-muted block mb-1">Enlace Directo de Entrada:</span>
                      <a
                        href={directLoginUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-electric hover:underline break-all font-mono text-[11px]"
                      >
                        <span>{directLoginUrl}</span>
                        <ExternalLink className="size-3 shrink-0" />
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-2.5 pt-2 border-t border-line">
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard?.writeText(shareText);
                        setCopiedCredentials(true);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 rounded-msr border border-line bg-snow px-4 py-2 text-xs font-semibold text-carbon hover:bg-surface transition-colors"
                    >
                      <Copy className="size-3.5" />
                      <span>{copiedCredentials ? "¡Accesos Copiados!" : "Copiar Accesos"}</span>
                    </button>

                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 rounded-msr bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-500 transition-colors"
                    >
                      <MessageSquare className="size-3.5" />
                      <span>Enviar Accesos por WhatsApp</span>
                    </a>

                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setCreatedCredentials(null)}
                    >
                      Listo
                    </Button>
                  </div>
                </>
              );
            })()}
          </Card>
        </div>
      ) : null}

      {/* Modal de Cambio de Plan */}
      {planModalTenant ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div>
                <h2 className="font-display text-lg font-bold text-carbon">
                  Cambiar Plan de Suscripción
                </h2>
                <p className="text-xs text-muted">
                  {planModalTenant.name} ({planModalTenant.slug})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPlanModalTenant(null)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="space-y-3">
              {plans.map((p) => {
                const isSelected = selectedNewPlan === p.code;
                const isCurrent = planModalTenant.plan_code === p.code;

                return (
                  <div
                    key={p.code}
                    onClick={() => setSelectedNewPlan(p.code)}
                    className={`cursor-pointer rounded-msr border p-3 transition-all ${
                      isSelected
                        ? "border-electric bg-electric/10 text-carbon font-semibold"
                        : "border-line bg-surface/50 text-carbon hover:bg-surface"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-carbon flex items-center gap-2">
                        {p.name}
                        {isCurrent ? (
                          <span className="rounded-full bg-surface border border-line px-2 py-0.5 text-[10px] text-muted">
                            Actual
                          </span>
                        ) : null}
                      </div>
                      <div className="text-sm font-bold text-carbon">
                        <Money cents={p.price_monthly_cents} />
                        <span className="text-xs text-muted font-normal">/mes</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-muted">{p.description}</p>
                    <div className="mt-2 flex gap-4 text-[11px] text-muted">
                      <span>{p.max_users} usuarios</span>
                      <span>{p.max_branches} sucursales</span>
                      <span>{p.max_products} productos</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-line">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPlanModalTenant(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() =>
                  planMutation.mutate({
                    tenantId: planModalTenant.id,
                    planCode: selectedNewPlan,
                  })
                }
                disabled={planMutation.isPending}
              >
                {planMutation.isPending ? "Guardando..." : "Aplicar Cambio"}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {/* Modal de Configuración de Servicios */}
      {servicesModalTenant ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div>
                <h2 className="font-display text-lg font-bold text-carbon flex items-center gap-2">
                  <SlidersHorizontal className="size-5 text-electric" />
                  Servicios del Negocio
                </h2>
                <p className="text-xs text-muted">
                  {servicesModalTenant.name} ({servicesModalTenant.slug})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setServicesModalTenant(null)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <p className="text-xs text-muted">
              Active o desactive los servicios a los que este negocio tiene acceso. Los cambios se aplican de forma inmediata.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                servicesMutation.mutate({
                  tenantId: servicesModalTenant.id,
                  services: tenantServicesForm,
                });
              }}
              className="space-y-3"
            >
              <label
                className={`flex items-start gap-3 rounded-msr border p-3 cursor-pointer transition-colors ${
                  tenantServicesForm.has_pos
                    ? "border-emerald-500/50 bg-emerald-500/10"
                    : "border-line bg-surface/50 opacity-70"
                }`}
              >
                <input
                  type="checkbox"
                  checked={tenantServicesForm.has_pos}
                  onChange={(e) =>
                    setTenantServicesForm((prev) => ({ ...prev, has_pos: e.target.checked }))
                  }
                  className="mt-0.5 rounded border-line bg-snow text-emerald-500 focus:ring-0"
                />
                <div>
                  <div className="text-sm font-semibold text-carbon">Punto de Venta</div>
                  <div className="text-xs text-muted">
                    Mostrador, catálogo de productos, cotizaciones y caja rápida.
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-msr border p-3 cursor-pointer transition-colors ${
                  tenantServicesForm.has_restaurant
                    ? "border-amber-500/50 bg-amber-500/10"
                    : "border-line bg-surface/50 opacity-70"
                }`}
              >
                <input
                  type="checkbox"
                  checked={tenantServicesForm.has_restaurant}
                  onChange={(e) =>
                    setTenantServicesForm((prev) => ({
                      ...prev,
                      has_restaurant: e.target.checked,
                    }))
                  }
                  className="mt-0.5 rounded border-line bg-snow text-amber-500 focus:ring-0"
                />
                <div>
                  <div className="text-sm font-semibold text-carbon">Restaurante</div>
                  <div className="text-xs text-muted">
                    Zonas, mesas, cuentas abiertas, comandas KDS, propinas y día operativo.
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-msr border p-3 cursor-pointer transition-colors ${
                  tenantServicesForm.has_workshop
                    ? "border-violet-500/50 bg-violet-500/10"
                    : "border-line bg-surface/50 opacity-70"
                }`}
              >
                <input
                  type="checkbox"
                  checked={tenantServicesForm.has_workshop}
                  onChange={(e) =>
                    setTenantServicesForm((prev) => ({
                      ...prev,
                      has_workshop: e.target.checked,
                    }))
                  }
                  className="mt-0.5 rounded border-line bg-snow text-violet-500 focus:ring-0"
                />
                <div>
                  <div className="text-sm font-semibold text-carbon">Taller Mecánico</div>
                  <div className="text-xs text-muted">
                    Órdenes de servicio técnico, vehículos, checklists y técnicos.
                  </div>
                </div>
              </label>

              <div className="rounded border border-line bg-surface p-3 text-[11px] text-muted">
                Nota: Todos los negocios tienen acceso garantizado a Clientes, Caja, Compras, Inventario, Reportes, Marca y Administración propia.
              </div>

              {servicesMutation.isError ? (
                <div className="rounded bg-rose-500/10 p-3 text-xs text-rose-500">
                  {(servicesMutation.error as Error)?.message || "Error al actualizar servicios"}
                </div>
              ) : null}

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setServicesModalTenant(null)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={servicesMutation.isPending}>
                  {servicesMutation.isPending ? "Guardando..." : "Guardar Servicios"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal: Migración a Esquema Dedicado (Opción B) */}
      {schemaUpgradeTenant ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md space-y-4 p-6 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-cyan-500/20 p-2 text-cyan-500">
                <Database className="size-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-carbon">Migrar a Esquema Dedicado</h3>
                <p className="text-xs text-muted">Opción B: Base de datos física independiente</p>
              </div>
            </div>

            <p className="text-sm text-carbon">
              ¿Deseas migrar el negocio <strong className="text-carbon font-semibold">{schemaUpgradeTenant.name}</strong> ({schemaUpgradeTenant.slug}) a su propio esquema de PostgreSQL?
            </p>

            <div className="rounded-msr border border-cyan-500/30 bg-cyan-500/10 p-3 text-xs text-cyan-800 dark:text-cyan-200 space-y-1">
              <p className="font-semibold">¿Qué sucederá?</p>
              <ul className="list-disc pl-4 space-y-0.5 text-muted dark:text-white/70">
                <li>Se creará el esquema <code className="text-cyan-600 dark:text-cyan-300 font-mono">tenant_{schemaUpgradeTenant.slug.replace(/-/g, "_")}</code>.</li>
                <li>Se clonarán todas las tablas y se copiarán sus productos, clientes, ventas y configuraciones.</li>
                <li>Se eliminarán los registros del esquema compartido para garantizar aislamiento total.</li>
              </ul>
            </div>

            {upgradeSchemaMutation.isError ? (
              <div className="rounded bg-rose-500/10 p-3 text-xs text-rose-500">
                {(upgradeSchemaMutation.error as Error)?.message || "Error al migrar esquema"}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2 border-t border-line">
              <Button
                variant="ghost"
                onClick={() => setSchemaUpgradeTenant(null)}
                disabled={upgradeSchemaMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => upgradeSchemaMutation.mutate(schemaUpgradeTenant.id)}
                disabled={upgradeSchemaMutation.isPending}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                icon={<Database className="size-4" />}
              >
                {upgradeSchemaMutation.isPending ? "Migrando..." : "Confirmar y Migrar"}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
